import { useRef } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Coin } from 'interfaces/MeshMaven';
import { useHistory } from 'react-router-dom';
import MeshMavenLoading from 'screens/common/MLCommentTextGenerationCompV1/MeshMavenLoading';
import MeshMavenError from 'screens/performanceReview/components/ContinuousFeedback/Drawers/common/MeshMaven/MeshMavenError';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

import { CheckCircle, CloseIcon } from 'assets/assetsV1/iconComponents';
import { ContentAnalyzerGradient } from 'assets/assetsV1/iconComponents/ContentAnalyzerGradient';

import { getQueryParams } from 'constants/helper';
import { sendReanalyzedMeshMavenContentEvent } from 'constants/mixpanelHelper';
import { CF_GEN_AI_BUTTON_TYPES, sendCFGenAIButtonClickedEvent } from 'constants/mixpanelHelpers/cfGenAIEvents';

import { Divider } from 'uiToolkitV1/Divider';
import GradientButton from 'uiToolkitV1/GradientButton/GradientButton';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import {
  useFeedbackAnalysisLoading,
  useCreateViolationAnalysisMutation,
  useRephraseTextLoading,
} from '../hooks/meshMavenDataHook';
import { useSelectAddViolationDrawerData } from '../slice/addViolation.slice';

const COIN_FEEDBACK_TEXT: Record<keyof Coin, string> = {
  context: 'Situation: Describes the specific time, place, and context where the behavior occurred.',
  observations: 'Behavior: Details the observed actions of the individual without interpretation or judgment.',
  impact: 'Impact: Explains the consequences or effects of the behavior on people, processes, or outcomes.',
};

const FeedbackAnalysis = () => {
  const { analysisError, selectedRevision } = useSelectAddViolationDrawerData();

  const feedbackAnalysis = selectedRevision.feedbackAnalysis ?? {};

  const isAnalyzeFetching = useFeedbackAnalysisLoading();
  const isRephraseFetching = useRephraseTextLoading();

  if (isAnalyzeFetching || isRephraseFetching) {
    return <MeshMavenLoading loadingText="Analyzing the comment via Maven" />;
  }

  if (analysisError) {
    return <MeshMavenError errorText={analysisError} />;
  }

  const positiveAnalysis = Object.keys(feedbackAnalysis).filter((k) => feedbackAnalysis[k] === 1 && k !== 'next_steps');

  const negativeAnalysis = Object.keys(feedbackAnalysis).filter((k) => feedbackAnalysis[k] === 0 && k !== 'next_steps');

  return (
    <Flex flexDir="column" py={3} px={5}>
      {selectedRevision.isReanalyzeEnabled && (
        <TextLabel bold color="alerts.in_progress" mb={2}>
          Text changes detected, click on "Reanalyse" for updated analysis.
        </TextLabel>
      )}
      <TextBodyMedium mb={3}>Maven analysed your text and here&apos;s what it found:</TextBodyMedium>
      {positiveAnalysis.length > 0 && (
        <>
          <TextLabel bold mb={1}>
            Your comment includes:
          </TextLabel>
          <Flex flexDir="column" gap={1}>
            {positiveAnalysis.map((k) => (
              <Flex alignItems="flex-start" gap={2}>
                <CheckCircle mt="3px" color="functional.2F8133" />
                <TextBodyMedium>{COIN_FEEDBACK_TEXT[k]}</TextBodyMedium>
              </Flex>
            ))}
          </Flex>
        </>
      )}
      {negativeAnalysis.length > 0 && (
        <>
          <TextLabel bold mt={3} mb={1}>
            Your comment could improve by including:
          </TextLabel>
          <Flex flexDir="column" gap={1}>
            {negativeAnalysis.map((k) => (
              <Flex alignItems="flex-start" gap={2}>
                <CloseIcon mt="3px" color="primary.FD812D" />
                <TextBodyMedium>{COIN_FEEDBACK_TEXT[k]}</TextBodyMedium>
              </Flex>
            ))}
          </Flex>
        </>
      )}
    </Flex>
  );
};

const MeshMavenAnalyze = () => {
  const parentRef = useRef(null);

  const { mutate: getFeedbackAnalysis } = useCreateViolationAnalysisMutation();
  const { rephrasedDescription, raisedFor } = useSelectAddViolationDrawerData();

  const isRephraseFetching = useRephraseTextLoading();
  const isAnalysisFetching = useFeedbackAnalysisLoading();

  const { revisionNumber, selectedRevision, revisions } = useSelectAddViolationDrawerData();

  const history = useHistory();
  const { source } = getQueryParams(history.location.search);

  if (!selectedRevision) {
    return null;
  }

  const handleReanalyze = () => {
    sendCFGenAIButtonClickedEvent({
      eventProperties: {
        source,
        type: CF_GEN_AI_BUTTON_TYPES.REANALYZE,
      },
    });
    sendReanalyzedMeshMavenContentEvent({
      eventProperties: {
        source: 'Share Feedback',
      },
    });
    getFeedbackAnalysis({
      revisions,
      revision: selectedRevision,
      revisionNumber,
      description: rephrasedDescription,
      employee: raisedFor,
    });
  };

  return (
    <Flex ref={parentRef} flexDir="column" borderRadius="8px" borderWidth="1px" borderColor="neutral.EBEFF3">
      <Flex p="10px 20px 6px" alignItems="center" w="100%" justifyContent="space-between">
        <TextBodyMedium bold w="100%">
          Disciplinary note analysis
        </TextBodyMedium>
        <Box
          sx={{
            button: {
              marginLeft: 'auto',
            },
          }}
        >
          <GradientButton
            // @ts-ignore
            text="Reanalyse"
            buttonProps={{
              StartIcon: ContentAnalyzerGradient,
            }}
            onClick={handleReanalyze}
            isDisabled={!selectedRevision.isReanalyzeEnabled || isRephraseFetching || isAnalysisFetching}
          />
        </Box>
      </Flex>
      <Divider color="neutral.EBEFF3" m={1} />
      <FeedbackAnalysis />
    </Flex>
  );
};

export { MeshMavenAnalyze };
